import { useEffect, useMemo } from 'react';
import { Polygon, useMap } from 'react-leaflet';
import * as turf from '@turf/turf';
import L from 'leaflet';
import { lv95ToWgs84ForOneCoordinate } from 'views/add-grid/map/coordinateTransforms';
import { calculateShadowCircle, calculateShadowDiagram } from './shadowParameters';

const hourlyFillOpacity = 0.14;

const ShadowCoverageDiagram = ({
  analysisArea,
  height,
  rule,
}: {
  analysisArea: Array<{ x: number; y: number }>;
  height: number;
  rule: string;
}) => {
  const map = useMap();
  const diagram = useMemo(() => calculateShadowDiagram(analysisArea, height, rule), [analysisArea, height, rule]);
  const viewportCircle = calculateShadowCircle(analysisArea, height, 'BS');
  const visibleHourly = useMemo(
    () =>
      diagram.hourly.map(({ hour, shape }) => ({
        hour,
        shape: diagram.overAllowedHours
          ? turf.difference(turf.featureCollection([shape, diagram.overAllowedHours]))
          : shape,
      })),
    [diagram]
  );
  const center = viewportCircle ? lv95ToWgs84ForOneCoordinate(viewportCircle.center.x, viewportCircle.center.y) : null;
  const polygons = (shape: (typeof diagram.hourly)[number]['shape']) =>
    (shape.geometry.type === 'Polygon' ? [shape.geometry.coordinates] : shape.geometry.coordinates).map((polygon) =>
      polygon.map((ring) => ring.map(([x, y]) => lv95ToWgs84ForOneCoordinate(x, y)))
    );

  useEffect(() => {
    if (!center || !viewportCircle) return;
    map.fitBounds(L.latLng(center).toBounds(viewportCircle.radius * 2), {
      animate: false,
      padding: [24, 24],
    });
  }, [center?.lat, center?.lng, map, viewportCircle?.radius]);

  return diagram.hourly.length ? (
    <>
      {visibleHourly.flatMap(({ hour, shape }) =>
        shape
          ? polygons(shape).map((positions, index) => (
              <Polygon
                key={`${hour}-${index}`}
                positions={positions}
                interactive={false}
                pathOptions={{
                  color: '#000000',
                  weight: 1,
                  opacity: 0.7,
                  fillColor: '#808080',
                  fillOpacity: hourlyFillOpacity,
                }}
              />
            ))
          : []
      )}
      {diagram.overAllowedHours &&
        polygons(diagram.overAllowedHours).map((positions, index) => (
          <Polygon
            key={`over-allowed-hours-${index}`}
            positions={positions}
            interactive={false}
            pathOptions={{
              color: '#000000',
              weight: 2,
              fillColor: '#808080',
              fillOpacity: 1 - (1 - hourlyFillOpacity) ** 4,
            }}
          />
        ))}
    </>
  ) : null;
};

export default ShadowCoverageDiagram;
